import type { PropsWithChildren } from 'react';
import React from 'react';

import { PrimaryButtonV3 } from '@fragment/ui/src/components/Button/PrimaryButtonV3/PrimaryButtonV3';
import { SecondaryButtonV3 } from '@fragment/ui/src/components/Button/SecondaryButtonV3/SecondaryButtonV3';
import { TextButton } from '@fragment/ui/src/components/Button/TextButton/TextButton';
import { Icon } from '@fragment/ui/src/components/Icon/BaseIcon/Icon';
import {
  LayoutCell,
  LayoutRow,
  TextCell,
} from '@fragment/ui/src/components/Payments/LayoutRow';

import { ToastProvider } from '../../wrappers/ToastProvider';

type PaymentViewLayoutProps = {
  title: string;
  primaryAction?: Action;
  secondaryAction?: Action;
  'data-testid'?: string;
  onBack?: () => void;
  backTitle?: string;
};

export type Action = {
  title: string;
  onClick: () => void;
  disabled?: boolean;
  disabledTooltipText?: string;
  'data-testid'?: string;
};

export const kActionButtonWidth =
  'w-full text-sm largeLaptop:w-f8 h-full largeLaptop:text-md';

const ActionButtons = ({
  primaryAction,
  secondaryAction,
}: {
  primaryAction?: Action;
  secondaryAction?: Action;
}) => (
  <div
    className="flex flex-row gap-x-f1 w-fit h-full justify-between"
    data-testid="invoice-action-buttons"
  >
    {secondaryAction && (
      <SecondaryButtonV3
        onClick={secondaryAction.onClick}
        className={kActionButtonWidth}
        disabled={secondaryAction.disabled}
        disabledTooltipText={secondaryAction.disabledTooltipText}
        data-testid={secondaryAction['data-testid']}
      >
        {secondaryAction.title}
      </SecondaryButtonV3>
    )}
    {primaryAction && (
      <PrimaryButtonV3
        onClick={primaryAction.onClick}
        className={kActionButtonWidth}
        disabled={primaryAction.disabled}
        disabledTooltipText={primaryAction.disabledTooltipText}
        data-testid={primaryAction['data-testid']}
      >
        {primaryAction.title}
      </PrimaryButtonV3>
    )}
  </div>
);

export const PaymentViewLayout = ({
  children,
  title,
  primaryAction,
  secondaryAction,
  onBack,
  backTitle,
  'data-testid': dataTestId,
}: PropsWithChildren<PaymentViewLayoutProps>) => (
  <ToastProvider>
    <div
      className="flex flex-col w-full h-fit min-h-[100dvh] font-sans py-f2"
      data-testid={dataTestId ?? 'invoice-layout'}
    >
      <LayoutRow isFirst height="medium">
        {!onBack && (
          <TextCell
            text={title}
            textSize="md"
            align="left"
            border="none"
            fullWidth
            data-testid="payment-view-header"
          />
        )}
        {onBack && (
          <LayoutCell border="none" align="top">
            <TextButton
              onClick={onBack}
              icon={<Icon type="left" size="md" primaryColor="gray" />}
              hoverIcon={<Icon type="left" size="md" primaryColor="black" />}
            >
              {backTitle && <span className="font-sans">{backTitle}</span>}
            </TextButton>
          </LayoutCell>
        )}
        <LayoutCell border="left" width={secondaryAction ? 'standard' : 'half'}>
          <ActionButtons
            primaryAction={primaryAction}
            secondaryAction={secondaryAction}
          />
        </LayoutCell>
      </LayoutRow>
      {children}
    </div>
  </ToastProvider>
);

type HeaderProps = {
  disabled?: boolean;
};

export const PaymentViewHeader = ({
  children,
  disabled,
}: PropsWithChildren<HeaderProps>) => (
  <LayoutRow height="medium" justify="between" hide={disabled}>
    {children}
  </LayoutRow>
);

export const PaymentViewLoading = ({
  message = 'Loading...',
}: {
  message?: string;
}) => (
  <PaymentViewLayout title="Loading...">
    <div className="text-main-500">{message}</div>
  </PaymentViewLayout>
);
